<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { accountId } = useAccount();

const dialogRef = ref(null);
const selectedPeriod = ref(null);
const notificationPhone = ref(''); // Armazena apenas dígitos (ex: 5567991234567)
const displayPhone = ref(''); // Armazena com máscara visual (ex: +55 (67) 99123-4567)
const phoneError = ref('');
const isLoading = ref(false);

const periodOptions = [
  { label: '1 dia', value: 1, webhookValue: '1 day' },
  { label: '3 dias', value: 3, webhookValue: '3 days' },
  { label: '7 dias', value: 7, webhookValue: '7 days' },
  { label: '15 dias', value: 15, webhookValue: '15 days' },
  { label: '30 dias', value: 30, webhookValue: '30 days' },
];

// Regex: 55 + DDD (2 digits) + number (8 or 9 digits)
// Format: 55DDXXXXXXXX (8 digits) or 55DD9XXXXXXXX (9 digits with 9)
const PHONE_REGEX_8 = /^55[1-9][0-9][2-9][0-9]{7}$/; // 12 digits total (without 9)
const PHONE_REGEX_9 = /^55[1-9][0-9]9[0-9]{8}$/; // 13 digits total (with 9)

const validatePhone = phone => {
  if (!phone) {
    phoneError.value = '';
    return true;
  }

  const cleanPhone = phone.replace(/\D/g, '');

  if (cleanPhone.length !== 12 && cleanPhone.length !== 13) {
    phoneError.value = t('EVALUATIONS_REPORT.PHONE.ERROR_LENGTH');
    return false;
  }

  const isValid =
    PHONE_REGEX_8.test(cleanPhone) || PHONE_REGEX_9.test(cleanPhone);

  if (!isValid) {
    phoneError.value = t('EVALUATIONS_REPORT.PHONE.ERROR_FORMAT');
    return false;
  }

  phoneError.value = '';
  return true;
};

const applyPhoneMask = digits => {
  // Apply mask: +55 (DD) NNNNN-NNNN or +55 (DD) NNNN-NNNN
  if (digits.length <= 2) {
    return `+${digits}`;
  }
  if (digits.length <= 4) {
    return `+${digits.slice(0, 2)} (${digits.slice(2)})`;
  }
  if (digits.length <= 9) {
    // 55 + DDD + até 5 dígitos
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4)}`;
  }
  if (digits.length <= 13) {
    // Format: +55 (DD) NNNNN-NNNN (9 digits) or +55 (DD) NNNN-NNNN (8 digits)
    const ddd = digits.slice(2, 4);
    const number = digits.slice(4);

    if (number.length <= 4) {
      return `+${digits.slice(0, 2)} (${ddd}) ${number}`;
    }
    if (number.length === 8) {
      // 8 digits: NNNN-NNNN
      return `+${digits.slice(0, 2)} (${ddd}) ${number.slice(0, 4)}-${number.slice(4)}`;
    }
    // 9 digits: NNNNN-NNNN
    return `+${digits.slice(0, 2)} (${ddd}) ${number.slice(0, 5)}-${number.slice(5)}`;
  }
  return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9, 13)}`;
};

const formatPhoneInput = event => {
  const value = event.target.value.replace(/\D/g, '');
  notificationPhone.value = value;
  displayPhone.value = applyPhoneMask(value);
  validatePhone(value);
};

const getWebhookPeriod = () => {
  const option = periodOptions.find(o => o.value === selectedPeriod.value);
  return option?.webhookValue || `${selectedPeriod.value} days`;
};

const canSubmit = computed(() => {
  return (
    selectedPeriod.value !== null &&
    (notificationPhone.value.length === 12 ||
      notificationPhone.value.length === 13) &&
    !phoneError.value
  );
});

const open = () => {
  selectedPeriod.value = null;
  notificationPhone.value = '';
  displayPhone.value = '';
  phoneError.value = '';
  isLoading.value = false;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const generateReport = async () => {
  if (!canSubmit.value) return;

  isLoading.value = true;

  try {
    const response = await fetch(
      'https://n8nwh-dev.eixox.com/webhook/Gerar-relatorio',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: accountId.value,
          periodo: getWebhookPeriod(),
          telefone: notificationPhone.value,
        }),
      }
    );

    if (response.ok) {
      useAlert(t('EVALUATIONS_REPORT.SUCCESS'));
      close();
    } else {
      useAlert(t('EVALUATIONS_REPORT.ERROR'));
    }
  } catch {
    useAlert(t('EVALUATIONS_REPORT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('EVALUATIONS_REPORT.TITLE')"
    :description="t('EVALUATIONS_REPORT.DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="md"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('EVALUATIONS_REPORT.SELECT_PERIOD') }}
        </label>
        <div class="grid grid-cols-5 gap-2">
          <button
            v-for="option in periodOptions"
            :key="option.value"
            type="button"
            class="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="[
              selectedPeriod === option.value
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
            ]"
            @click="selectedPeriod = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('EVALUATIONS_REPORT.PHONE.LABEL') }}
        </label>
        <div class="flex flex-col gap-1">
          <input
            v-model="displayPhone"
            type="text"
            inputmode="numeric"
            maxlength="19"
            :placeholder="t('EVALUATIONS_REPORT.PHONE.PLACEHOLDER')"
            class="w-full rounded-lg border px-3 py-2 text-sm"
            :class="[
              phoneError
                ? 'border-r-red-500 focus:border-r-red-500'
                : 'border-n-weak focus:border-n-brand',
            ]"
            @input="formatPhoneInput"
          />
          <span v-if="phoneError" class="text-xs text-r-red-500">
            {{ phoneError }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('EVALUATIONS_REPORT.PHONE.HINT') }}
          </span>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          class="w-full"
          type="button"
          @click="close"
        />
        <Button
          color="blue"
          :label="t('EVALUATIONS_REPORT.GENERATE')"
          class="w-full"
          :is-loading="isLoading"
          :disabled="!canSubmit || isLoading"
          type="button"
          @click="generateReport"
        />
      </div>
    </template>
  </Dialog>
</template>
